import type { RouteComponentProps } from '@main/core-ui';
import {
  CenteredDiv,
  Container,
  ErrorText,
  logger,
  Skeleton,
  withRouter,
} from '@main/core-ui';
import { Button } from '@main/pc-core-components';
import React from 'react';
import type { IntlShape } from 'react-intl';
import { injectIntl } from 'react-intl';
import type { Location } from 'react-router-dom';

import { errorBoundaryMessages } from './messages';

/**
 * ErrorBoundaryProps
 */
export interface ErrorBoundaryProps extends RouteComponentProps {
  /** Intl */
  intl: IntlShape;
  /** The current page location */
  location: Location;
  /** The child contents */
  children: React.ReactNode;
}

/**
 * ErrorBoundary state
 */
export interface ErrorBoundaryState {
  /** Indicates if an error has occurred */
  hasError: boolean;
  /** Indicates if loading */
  loading: boolean;
}

/**
 * Bound the ui with an error boundary
 *
 * TODO send to datadog
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  /** State */
  public state = { hasError: false, loading: false };

  /**
   * If the pathname changes, allow the error to be forgotten
   *
   * @param newProps - The new ErrorBoundary props
   */
  public UNSAFE_componentWillReceiveProps(newProps: ErrorBoundaryProps): void {
    const { location } = this.props;
    if (newProps.location.pathname !== location.pathname) {
      this.setState({ loading: true });
      setTimeout(
        () => this.setState({ hasError: false, loading: false }),
        1000,
      );
    }
  }

  /**
   * Go back to the previous page
   *
   */
  public back = (): void => {
    this.props.navigate(-1);
    this.setState({ loading: true });
    setTimeout(() => this.setState({ hasError: false, loading: false }), 1000);
  };

  /**
   * Catch the unexpected error and set the state to hasError:true
   *
   * @param error - The error that occurred
   * @param info - The information associated with the error
   */
  public componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({ hasError: true });
    logger.error(error, info);
    localStorage.clear();
  }

  /**
   * Render the ErrorBoundary container
   *
   * @returns The JSX for the ErrorBoundary container
   */
  public render(): React.ReactNode {
    const { hasError, loading } = this.state;
    const {
      children,
      intl: { formatMessage },
    } = this.props;
    if (hasError) {
      // You can render any custom fall-back UI
      return (
        <Container>
          <CenteredDiv>
            <h3>
              <ErrorText>
                {formatMessage(errorBoundaryMessages.header)}
              </ErrorText>
            </h3>
            {loading && <Skeleton />}
            <Button onClick={this.back} buttonType="secondary">
              {formatMessage(errorBoundaryMessages.back)}
            </Button>
          </CenteredDiv>
        </Container>
      );
    }
    return children;
  }
}

export const ErrorBoundaryWithInl = injectIntl(ErrorBoundary);
export default withRouter(ErrorBoundaryWithInl);
